import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, ShieldCheck, Warning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (v) => {
  if (v == null) return "—";
  try {
    return `${Number(v).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  } catch { return `${v} €`; }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

/**
 * Página pública — sin auth. Valida el informe escaneado vía QR.
 * Branding alineado al informe PDF: navy + naranja Ekspertium (#E85D2C).
 */
export default function VerifyInforme() {
  const { attId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/verify/${attId}`);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({
          loading: false, data: null,
          error: e?.response?.data?.detail || "Informe no encontrado o enlace no válido",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [attId]);

  return (
    <div className="min-h-screen bg-[#0F172A] text-white flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Branding Ekspertium */}
        <div className="flex items-center gap-2.5 justify-center mb-8">
          <div className="w-8 h-8 rounded-md bg-[#E85D2C] flex items-center justify-center shadow-lg shadow-[#E85D2C]/30">
            <ShieldCheck className="w-5 h-5 text-white" weight="bold" />
          </div>
          <span className="font-bold tracking-tight text-xl">
            Eksp<span className="text-[#E85D2C]">ertium</span>
          </span>
        </div>

        {state.loading && (
          <div className="text-center text-slate-400 text-sm">Verificando documento...</div>
        )}

        {state.error && (
          <div
            data-testid="verify-error"
            className="rounded-lg border border-red-500/40 bg-red-500/10 p-6 text-center"
          >
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" weight="fill" />
            <div className="text-lg font-bold mb-1">Documento no verificable</div>
            <div className="text-sm text-slate-300">{state.error}</div>
          </div>
        )}

        {state.data && (() => {
          const rehuse = state.data.rehuse || "ninguno";
          const tipo = state.data.tipo || "final";
          const frameCls = {
            ninguno: "border-emerald-500/40 bg-emerald-500/5",
            parcial: "border-[#E85D2C] bg-[#E85D2C]/10 ring-4 ring-[#E85D2C]/20",
            total:   "border-red-600 bg-red-600/10 ring-4 ring-red-600/25",
          }[rehuse];
          const headerCls = {
            ninguno: "border-emerald-500/30 bg-emerald-500/10",
            parcial: "border-[#E85D2C]/60 bg-[#E85D2C]/20",
            total:   "border-red-600/60 bg-red-600/25",
          }[rehuse];
          const headerIconColor = {
            ninguno: "text-emerald-400",
            parcial: "text-[#FFB892]",
            total:   "text-red-300",
          }[rehuse];
          const headerTitleColor = {
            ninguno: "text-emerald-300",
            parcial: "text-[#FFB892]",
            total:   "text-red-200",
          }[rehuse];
          const HeaderIcon = rehuse === "ninguno" ? CheckCircle : Warning;
          const headerTitle = {
            ninguno: "Documento verificado",
            parcial: "Documento verificado · REHÚSE PARCIAL",
            total:   "Documento verificado · REHÚSE TOTAL",
          }[rehuse];
          const headerSubtitle = rehuse === "ninguno"
            ? `Informe ${tipo} auténtico`
            : `Informe ${tipo} emitido con rehúse ${rehuse}`;
          return (
            <div
              data-testid="verify-ok"
              data-rehuse={rehuse}
              className={`rounded-lg border-2 overflow-hidden ${frameCls}`}
            >
              <div className={`flex items-center gap-3 px-6 py-4 border-b ${headerCls}`}>
                <HeaderIcon className={`w-7 h-7 flex-shrink-0 ${headerIconColor}`} weight="fill" />
                <div className="flex-1">
                  <div className={`font-bold ${headerTitleColor}`}>{headerTitle}</div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-widest">
                    {headerSubtitle}
                  </div>
                </div>
                <ShieldCheck className="w-6 h-6 text-white/30" weight="bold" />
              </div>

              <dl className="px-6 py-5 space-y-3 text-[13px]">
                <Row label="Aseguradora" value={state.data.aseguradora} />
                <Row label="Nº siniestro" value={state.data.numero_siniestro} mono />
                <Row label="Fecha siniestro" value={fmtDate(state.data.fecha_siniestro)} />
                <Row label="Fecha cierre" value={fmtDate(state.data.fecha_cierre)} />
                <Row label="Perito" value={state.data.perito} />
                {rehuse !== "ninguno" && (
                  <Row
                    label="Rehúse"
                    value={rehuse === "parcial" ? "PARCIAL" : "TOTAL"}
                    strong
                    accent={rehuse === "parcial"}
                    red={rehuse === "total"}
                  />
                )}
                {rehuse !== "ninguno" && state.data.rehuse_motivo && (
                  <div
                    data-testid="verify-rehuse-motivo"
                    className={`mt-3 rounded-md border p-3 ${
                      rehuse === "parcial"
                        ? "border-[#E85D2C]/40 bg-[#E85D2C]/10"
                        : "border-red-500/40 bg-red-500/10"
                    }`}
                  >
                    <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${
                      rehuse === "parcial" ? "text-[#FFB892]" : "text-red-300"
                    }`}>
                      Motivo del rehúse
                    </div>
                    <div className="text-[13px] text-white whitespace-pre-wrap">
                      {state.data.rehuse_motivo}
                    </div>
                  </div>
                )}
                <div className="pt-3 mt-3 border-t border-slate-700 space-y-2">
                  <Row label="Importe tasación" value={fmtEur(state.data.importe_tasacion)} mono strong accent />
                  <Row label="Importe minuta" value={fmtEur(state.data.importe_minuta)} mono strong />
                </div>
              </dl>

              <div className="px-6 py-3 bg-slate-950/60 border-t border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 text-center">
                  Emitido por {state.data.issuer} · {fmtDate(state.data.issued_at)}
                </div>
              </div>
            </div>
          );
        })()}

        <div className="mt-6 text-center text-[11px] text-slate-500">
          Verificación pública · <span className="text-[#E85D2C]">Ekspertium</span>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, strong, accent, red }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[10.5px] uppercase tracking-widest text-slate-400 font-semibold">
        {label}
      </dt>
      <dd className={`text-right ${mono ? "font-mono" : ""} ${strong ? "text-[15px] font-bold" : ""} ${
        red ? "text-red-300" : accent ? "text-[#E85D2C]" : "text-white"
      }`}>
        {value || "—"}
      </dd>
    </div>
  );
}
